import { useEffect, useRef, useState } from 'react';
import { MoreVertical, Plus } from 'lucide-react';
import { MainLayout } from '@/layouts/MainLayout';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SimpleSelect } from '@/components/ui/SimpleSelect';
import { SimpleSwitch } from '@/components/ui/SimpleSwitch';
import { FileIcon } from '@/components/ui/FileIcon';
import { DownloadModal } from '@/components/ui/DownloadModal';
import { Badge, DeleteConfirmModal, ProgressSpinner } from '@/components/ui';
import { ScrollToTopButton, PageHeader, Container } from '@/components/ui';
import { FileReportActionsSheet } from '@/components/fileReports/FileReportActionsSheet';
import { FileReportDialog } from '@/components/fileReports/FileReportDialog';
import { ReplaceFileDialog } from '@/components/fileReports/ReplaceFileDialog';
import { useToast } from '@/contexts/ToastContext';
import { useFileReportsStore } from '@/store';
import { useFileReports } from '@/hooks/useFileReports';
import { useHasRole } from '@/permissions/hooks';
import { UserRole } from '@/permissions/types';
import { ReportsService } from '@/services/reports.service';
import { getWriteErrorMessage } from '@/utils/apiError';
import { isManagedFileReport } from '@/types/reports.types';
import type { FileReport, ManagedFileReport } from '@/types/reports.types';
import { clsx } from 'clsx';

interface ReportsPageProps {
  category?: string;
  title?: string;
}

interface AdminDialog {
  isOpen: boolean;
  report: ManagedFileReport | null;
}

const closedDialog: AdminDialog = { isOpen: false, report: null };
const openDialog = (report: ManagedFileReport | null): AdminDialog => ({ isOpen: true, report });
const closeDialog = (dialog: AdminDialog): AdminDialog => ({ ...dialog, isOpen: false });

export const ReportsPage = ({ category, title = 'File Reports' }: ReportsPageProps = {}) => {
  const { viewMode, categories, categoryOptions, setViewMode } = useFileReportsStore();
  // TIX-347: staff with this role add, edit, replace, hide and delete files on this page.
  const isAdmin = useHasRole(UserRole.REPORTS_FILES_ADMIN);
  const { showToast } = useToast();
  const {
    files,
    isLoading,
    isLoadingMore,
    error,
    hasNextPage,
    loadReports,
    loadMore,
    loadCategories,
    handleDownload,
    updateFilters,
  } = useFileReports({ admin: isAdmin });

  const [selectedFile, setSelectedFile] = useState<FileReport | null>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const loadingRef = useRef<HTMLDivElement>(null);
  const initialLoadCompleteRef = useRef(false);
  // Track previous filter values to detect actual changes
  const prevFiltersRef = useRef({ searchQuery: '', selectedCategory: '' });

  // Admin state
  const [showHidden, setShowHidden] = useState(false);
  const [menuReport, setMenuReport] = useState<ManagedFileReport | null>(null);
  // Each dialog keeps its report while closing so the content does not change mid-transition.
  // A null report in the form dialog means "add".
  const [formDialog, setFormDialog] = useState<AdminDialog>(closedDialog);
  const [replaceDialog, setReplaceDialog] = useState<AdminDialog>(closedDialog);
  const [deleteDialog, setDeleteDialog] = useState<AdminDialog>(closedDialog);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load initial data only once
  useEffect(() => {
    if (!initialLoadCompleteRef.current) {
      // If category prop is provided, set it as initial filter
      if (category) {
        setSelectedCategory(category);
        updateFilters({ category, includeHidden: false });
        prevFiltersRef.current.selectedCategory = category;
      } else {
        updateFilters({ category: '', search: '', includeHidden: false });
      }
      loadReports(true);
      loadCategories();
      // Mark initial load as complete after a short delay to ensure state is settled
      setTimeout(() => {
        initialLoadCompleteRef.current = true;
      }, 100);
    }
  }, [loadReports, loadCategories, category, updateFilters]);

  // Load data when filters change (only after user interaction)
  useEffect(() => {
    // Skip if initial load hasn't completed yet
    if (!initialLoadCompleteRef.current) {
      return;
    }

    // Check if values actually changed from previous values
    const filtersChanged =
      searchQuery !== prevFiltersRef.current.searchQuery ||
      selectedCategory !== prevFiltersRef.current.selectedCategory;

    if (!filtersChanged) {
      return;
    }

    // Update previous values
    prevFiltersRef.current = { searchQuery, selectedCategory };

    // Debounce the filter application
    const timeoutId = setTimeout(async () => {
      setIsFiltering(true);
      try {
        updateFilters({ search: searchQuery, category: selectedCategory });
        await loadReports(true);
      } finally {
        setIsFiltering(false);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCategory]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextPage, isLoadingMore, isLoading]);

  const handleFileClick = (file: FileReport) => {
    setSelectedFile(file);
    setIsDownloadModalOpen(true);
  };

  const handleShowHiddenChange = (checked: boolean) => {
    setShowHidden(checked);
    updateFilters({ includeHidden: checked });
    loadReports(true);
  };

  const handleSaved = (operation: 'created' | 'updated', caption: string) => {
    showToast(`"${caption}" ${operation === 'created' ? 'added' : 'saved'}`, {
      variant: 'success',
    });
    loadReports(true);
    // A first file in a category adds it to the filter for everyone else.
    if (operation === 'created') loadCategories();
  };

  const handleToggleVisibility = async (report: ManagedFileReport) => {
    try {
      await ReportsService.setVisibility(report.pageId, !report.visible);
      showToast(
        report.visible
          ? `"${report.fileCaption}" is now hidden from customers`
          : `"${report.fileCaption}" is now visible to customers`,
        { variant: 'success' }
      );
      loadReports(true);
    } catch (err) {
      showToast(getWriteErrorMessage(err, 'The visibility could not be changed.'), {
        variant: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    const report = deleteDialog.report;
    if (!report) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await ReportsService.deleteFileReport(report.pageId);
      showToast(`"${report.fileCaption}" deleted`, { variant: 'success' });
      setDeleteDialog(closeDialog);
      loadReports(true);
    } catch (err) {
      setDeleteError(getWriteErrorMessage(err, 'The file report could not be deleted.'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Shared by grid cards and list rows. stopPropagation keeps the row's download modal shut.
  const renderAdminMenuButton = (file: FileReport, className?: string) =>
    isAdmin && isManagedFileReport(file) ? (
      <button
        type="button"
        aria-label={`Manage ${file.fileCaption}`}
        title="Manage file"
        className={clsx(
          'rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:bg-white/20 dark:hover:text-white sm:p-2',
          className
        )}
        onClick={(e) => {
          e.stopPropagation();
          setMenuReport(file);
        }}
      >
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </button>
    ) : null;

  const renderHiddenBadge = (file: FileReport) =>
    isManagedFileReport(file) && !file.visible ? (
      <Badge variant="warning" size="sm">
        Hidden
      </Badge>
    ) : null;

  const fileKey = (file: FileReport) => (isManagedFileReport(file) ? file.pageId : file.fileId);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderGridView = () => (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
      {files.map((file) => (
        <div
          key={fileKey(file)}
          className="group relative flex cursor-pointer flex-col overflow-hidden rounded-lg bg-white p-3 shadow-md transition-all duration-200 hover:shadow-xl dark:bg-gradient-to-br dark:from-[#2d3748] dark:to-[#1a202c] sm:p-4"
          onClick={() => handleFileClick(file)}
        >
          {/* Download Icon (and admin menu) - Top Right */}
          <div className="absolute right-2 top-2 flex items-center gap-1 sm:right-3 sm:top-3">
            <button
              className="rounded-lg bg-gray-100 p-1.5 text-gray-600 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-900 group-hover:opacity-100 dark:bg-white/10 dark:text-gray-400 dark:hover:bg-white/20 dark:hover:text-white sm:p-2"
              onClick={(e) => {
                e.stopPropagation();
                handleFileClick(file);
              }}
            >
              <svg
                className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>
            {renderAdminMenuButton(file, 'bg-gray-100 dark:bg-white/10')}
          </div>

          {/* File Icon */}
          <div className="mb-2 sm:mb-3">
            <FileIcon fileName={file.fileName} size="lg" />
          </div>

          {/* File Title */}
          <h3 className="mb-1 line-clamp-2 text-sm font-medium text-gray-900 dark:text-white sm:text-base">
            {file.fileCaption}
          </h3>

          {/* Category */}
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {file.fileCategory}
            {renderHiddenBadge(file)}
          </span>

          {/* Bottom Info - pushed to bottom */}
          <div className="mt-auto flex items-center justify-between border-t border-gray-200 pt-2 text-[10px] text-gray-500 dark:border-white/10 dark:text-gray-400 sm:pt-3 sm:text-xs">
            <span>{formatDate(file.fileDate)}</span>
            <span className="uppercase">
              {file.fileName.split('.').pop()?.substring(0, 4) || 'FILE'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow-md dark:divide-white/10 dark:bg-gradient-to-br dark:from-[#2d3748] dark:to-[#1a202c]">
      {files.map((file) => (
        <div
          key={fileKey(file)}
          className="flex cursor-pointer items-center gap-3 px-3 py-3.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/5 sm:px-4 sm:py-3"
          onClick={() => handleFileClick(file)}
        >
          <FileIcon fileName={file.fileName} size="sm" />
          <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
            {file.fileCaption}
          </h3>
          {renderHiddenBadge(file)}
          <span className="hidden shrink-0 text-xs text-gray-500 dark:text-gray-400 sm:block">
            {file.fileCategory}
          </span>
          <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
            {formatDate(file.fileDate)}
          </span>
          {renderAdminMenuButton(file, '-my-1.5 shrink-0')}
        </div>
      ))}
    </div>
  );

  return (
    <MainLayout>
      <Container>
        <div className="space-y-6">
          {/* Header */}
          <PageHeader title={title} />

          {/* Filters Bar - Matches Angular design */}
          <div className="flex flex-col gap-3 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 p-4 dark:from-[#1e293b] dark:to-[#334155] sm:flex-row sm:items-center">
            {/* Search Input - Takes most space */}
            <div className="relative w-full flex-1 sm:max-w-2xl">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                {isFiltering ? (
                  <div className="h-4 w-4 text-gray-400">
                    <ProgressSpinner size="sm" />
                  </div>
                ) : (
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                )}
              </div>
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full rounded-full border-0 bg-white py-2.5 pl-10 pr-4 text-gray-900 placeholder-gray-500 shadow-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/10 dark:text-white dark:placeholder-gray-400 dark:focus:bg-white/20 dark:focus:ring-white/30"
              />
            </div>

            {/* Right side: Category + View Toggle */}
            <div className="flex w-full items-center justify-between gap-3 sm:ml-auto sm:w-auto">
              {/* Category Dropdown */}
              {!category && (
                <div className="flex-1 sm:w-48 sm:flex-none">
                  <SimpleSelect
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    disabled={isFiltering}
                    placeholder="All Categories"
                    options={[
                      { value: '', label: 'All Categories' },
                      ...categories.map((cat) => ({ value: cat, label: cat })),
                    ]}
                    className="rounded-lg border-0 bg-white text-gray-900 shadow-sm focus:ring-primary-500 dark:bg-white/10 dark:text-white dark:focus:bg-white/20 dark:focus:ring-white/30"
                  />
                </div>
              )}

              {/* View Toggle Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={clsx(
                    'rounded-lg p-2.5 transition-colors',
                    viewMode === 'grid'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/20'
                  )}
                  title="Grid View"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={clsx(
                    'rounded-lg p-2.5 transition-colors',
                    viewMode === 'list'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/20'
                  )}
                  title="List View"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Admin controls (REPORTS_FILES_ADMIN) */}
          {isAdmin && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SimpleSwitch
                inline
                checked={showHidden}
                onChange={handleShowHiddenChange}
                label="Show hidden files"
                disabled={isFiltering}
              />
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setFormDialog(openDialog(null))}
              >
                Add file
              </Button>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-center gap-3 text-red-800 dark:text-red-200">
                <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            </Card>
          )}

          {/* Content */}
          {isLoading && files.length === 0 ? (
            <Card>
              <div className="flex flex-col items-center justify-center py-12">
                <ProgressSpinner />
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading reports...</p>
              </div>
            </Card>
          ) : files.length === 0 ? (
            <Card>
              <div className="py-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                  No files found
                </h3>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  Try adjusting your search criteria or refresh the page.
                </p>
              </div>
            </Card>
          ) : (
            <>
              <div className="relative">
                {/* Loading overlay for filtering */}
                {(isLoading || isFiltering) && files.length > 0 && (
                  <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/50 pt-8 dark:bg-dark-900/50">
                    <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-lg dark:bg-dark-800">
                      <ProgressSpinner size="sm" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {isFiltering ? 'Applying filters...' : 'Loading...'}
                      </span>
                    </div>
                  </div>
                )}

                {viewMode === 'grid' ? renderGridView() : renderListView()}
              </div>

              {/* Load More Trigger */}
              {hasNextPage && (
                <div ref={loadingRef} className="flex justify-center py-8">
                  {isLoadingMore ? (
                    <div className="flex items-center gap-3">
                      <ProgressSpinner size="sm" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Loading more files...
                      </span>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={loadMore}>
                      Load More Files
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Scroll to Top Button */}
          {files.length > 0 && <ScrollToTopButton />}

          {/* Download Modal */}
          <DownloadModal
            file={selectedFile}
            isOpen={isDownloadModalOpen}
            onClose={() => {
              setIsDownloadModalOpen(false);
              setSelectedFile(null);
            }}
            onDownload={handleDownload}
          />

          {isAdmin && (
            <>
              <FileReportActionsSheet
                report={menuReport}
                onClose={() => setMenuReport(null)}
                onEdit={(report) => setFormDialog(openDialog(report))}
                onReplace={(report) => setReplaceDialog(openDialog(report))}
                onToggleVisibility={handleToggleVisibility}
                onDelete={(report) => {
                  setDeleteError(null);
                  setDeleteDialog(openDialog(report));
                }}
              />
              <FileReportDialog
                isOpen={formDialog.isOpen}
                onClose={() => setFormDialog(closeDialog)}
                report={formDialog.report}
                categoryOptions={categoryOptions}
                defaultCategory={category}
                onSaved={handleSaved}
              />
              <ReplaceFileDialog
                isOpen={replaceDialog.isOpen}
                onClose={() => setReplaceDialog(closeDialog)}
                report={replaceDialog.report}
                onReplaced={(caption) => {
                  showToast(`File replaced on "${caption}"`, { variant: 'success' });
                  loadReports(true);
                }}
              />
              <DeleteConfirmModal
                isOpen={deleteDialog.isOpen}
                onClose={() => {
                  if (!isDeleting) setDeleteDialog(closeDialog);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete file report"
                message="The file will no longer be shown to anyone, here or in the legacy extranet."
                itemName={deleteDialog.report?.fileCaption}
                loading={isDeleting}
                error={deleteError}
              />
            </>
          )}
        </div>
      </Container>
    </MainLayout>
  );
};
